import { useEffect, useMemo, useState } from "react";
import { listLeads } from "@/lib/api";
import {
  CalendarDays,
  Clock,
  Phone,
  MessageCircle,
  Loader2,
  ChevronDown,
} from "lucide-react";

const SLOT_ORDER = {
  "10 AM – 12 PM": 0,
  "12 – 2 PM": 1,
  "2 – 4 PM": 2,
  "4 – 6 PM": 3,
};

const inr = (n) => "₹" + new Intl.NumberFormat("en-IN").format(n);

const istToday = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const istPlus = (days) =>
  new Date(Date.now() + days * 86400000).toLocaleDateString("en-CA", {
    timeZone: "Asia/Kolkata",
  });

function dayHeading(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d); // local date — no timezone shift
  const base = date.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  if (dateStr === istToday()) return { base, badge: "Today" };
  if (dateStr === istPlus(1)) return { base, badge: "Tomorrow" };
  return { base, badge: null };
}

export default function AdminVisits() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setRows(await listLeads({ limit: 1000 }));
      } catch (e) {
        setErr(e.message || "Failed to load visits");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const { upcomingDays, pastDays, byDay } = useMemo(() => {
    const visits = rows.filter((r) => r.preferred_date);
    const grouped = {};
    visits.forEach((v) => {
      (grouped[v.preferred_date] = grouped[v.preferred_date] || []).push(v);
    });
    Object.values(grouped).forEach((list) =>
      list.sort(
        (a, b) =>
          (SLOT_ORDER[a.preferred_slot] ?? 9) - (SLOT_ORDER[b.preferred_slot] ?? 9)
      )
    );
    const today = istToday();
    const days = Object.keys(grouped).sort();
    return {
      byDay: grouped,
      upcomingDays: days.filter((d) => d >= today),
      pastDays: days.filter((d) => d < today).reverse(),
    };
  }, [rows]);

  const upcomingCount = upcomingDays.reduce(
    (n, d) => n + byDay[d].length,
    0
  );
  const todayCount = (byDay[istToday()] || []).length;

  const renderVisit = (v) => (
    <div
      key={v.id}
      data-testid={`visit-item-${v.id}`}
      className="flex items-center gap-3 flex-wrap rounded-xl hairline bg-white px-4 py-3"
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 text-orange-700 text-[11px] font-bold px-2.5 py-1 whitespace-nowrap">
        <Clock className="h-3 w-3" /> {v.preferred_slot || "Any time"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-ink text-sm truncate">{v.name}</div>
        <div className="text-xs text-muted-ink truncate">
          {v.project_type}
          {v.estimated_cost ? ` · ${inr(v.estimated_cost)}` : ""}
        </div>
      </div>
      <span className="font-mono text-sm text-ink-2">{v.phone}</span>
      <div className="flex gap-1.5 shrink-0">
        <a
          href={`tel:${v.phone}`}
          className="h-8 w-8 rounded-lg grid place-items-center bg-slate-100 hover:bg-slate-200"
          title="Call"
          data-testid={`visit-call-${v.id}`}
        >
          <Phone className="h-4 w-4 text-ink" />
        </a>
        <a
          href={`https://wa.me/${v.phone.replace(/[^\d]/g, "")}`}
          target="_blank"
          rel="noreferrer"
          className="h-8 w-8 rounded-lg grid place-items-center bg-green-100 hover:bg-green-200"
          title="WhatsApp"
          data-testid={`visit-whatsapp-${v.id}`}
        >
          <MessageCircle className="h-4 w-4 text-green-700" />
        </a>
      </div>
    </div>
  );

  const renderDay = (day, muted) => {
    const { base, badge } = dayHeading(day);
    return (
      <div key={day} data-testid={`visit-day-${day}`} className={muted ? "opacity-60" : ""}>
        <div className="flex items-center gap-2.5">
          <CalendarDays className="h-4 w-4 text-orange-600" />
          <h3 className="font-display font-bold text-ink">{base}</h3>
          {badge && (
            <span className="rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
              {badge}
            </span>
          )}
          <span className="text-xs text-muted-ink">
            {byDay[day].length} visit{byDay[day].length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="mt-3 space-y-2">{byDay[day].map(renderVisit)}</div>
      </div>
    );
  };

  return (
    <div data-testid="admin-visits">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink">Site visits</h2>
          <p className="text-sm text-muted-ink mt-1">
            Upcoming customer site visits, grouped by day.
          </p>
        </div>
        <div className="flex gap-2" data-testid="visits-summary">
          <span className="rounded-full bg-white hairline px-3.5 py-1.5 text-xs font-bold text-ink">
            {todayCount} today
          </span>
          <span className="rounded-full bg-white hairline px-3.5 py-1.5 text-xs font-bold text-ink">
            {upcomingCount} upcoming
          </span>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white hairline shadow-soft p-5 md:p-6">
        {loading ? (
          <div className="p-16 grid place-items-center text-muted-ink">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : err ? (
          <div className="p-10 text-center text-red-600 font-semibold" data-testid="visits-error">
            {err}
          </div>
        ) : upcomingDays.length === 0 && pastDays.length === 0 ? (
          <div className="p-16 text-center text-muted-ink" data-testid="visits-empty">
            No site visits booked yet. Customers can pick a slot in the quote popup.
          </div>
        ) : (
          <div className="space-y-8">
            {upcomingDays.length === 0 && (
              <div className="text-sm text-muted-ink" data-testid="visits-no-upcoming">
                Nothing upcoming — all booked visits are in the past.
              </div>
            )}
            {upcomingDays.map((d) => renderDay(d, false))}

            {pastDays.length > 0 && (
              <div>
                <button
                  onClick={() => setShowPast((p) => !p)}
                  data-testid="visits-past-toggle"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-ink hover:text-ink"
                >
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform ${showPast ? "rotate-180" : ""}`}
                  />
                  Past visits ({pastDays.reduce((n, d) => n + byDay[d].length, 0)})
                </button>
                {showPast && (
                  <div className="mt-4 space-y-8">
                    {pastDays.map((d) => renderDay(d, true))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
